import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { 
  LayoutDashboard, Car, Users, Settings, FileText, Route, Palette,
  LogOut, Menu, X, ChevronRight, User, Headphones, DollarSign,
  MapPin, TrendingUp, Crown, PlusCircle, History, ClipboardList,
  Bell, MessageSquare, Wallet, TicketCheck, AlertTriangle, BarChart3,
  Camera, Phone, FolderOpen
} from "lucide-react";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

const menuConfig = {
  customer: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/customer" },
    { key: "book", label: "Book Ride", icon: PlusCircle, path: "/customer?tab=book" },
    { key: "rides", label: "My Rides", icon: Car, path: "/customer?tab=rides" },
    { key: "wallet", label: "Wallet", icon: Wallet, path: "/customer?tab=wallet" },
    { key: "referral", label: "Refer & Earn", icon: DollarSign, path: "/customer?tab=referral" },
    { key: "history", label: "History", icon: History, path: "/customer?tab=history" },
    { key: "photos", label: "Journey Photos", icon: Camera, path: "/customer?tab=photos" },
    { key: "tickets", label: "Support", icon: TicketCheck, path: "/customer?tab=tickets" },
    { key: "emergency", label: "Emergency", icon: Phone, path: "/customer?tab=emergency" },
    { key: "profile", label: "Profile", icon: User, path: "/customer?tab=profile" },
  ],
  driver: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/driver" },
    { key: "rides", label: "Assigned Rides", icon: Car, path: "/driver?tab=rides" },
    { key: "earnings", label: "Earnings", icon: DollarSign, path: "/driver?tab=earnings" },
    { key: "history", label: "Ride History", icon: History, path: "/driver?tab=history" },
    { key: "documents", label: "Documents", icon: FolderOpen, path: "/driver?tab=documents" },
    { key: "messages", label: "Messages", icon: MessageSquare, path: "/driver?tab=messages" },
    { key: "emergency", label: "Emergency", icon: Phone, path: "/driver?tab=emergency" },
    { key: "profile", label: "Profile", icon: User, path: "/driver?tab=profile" },
  ],
  admin: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
    { key: "rides", label: "Rides", icon: Route, path: "/admin?tab=rides" },
    { key: "drivers", label: "Drivers", icon: Car, path: "/admin?tab=drivers" },
    { key: "documents", label: "Documents", icon: FolderOpen, path: "/admin?tab=documents" },
    { key: "users", label: "Users", icon: Users, path: "/admin?tab=users" },
    { key: "wallet", label: "Wallet & Payments", icon: DollarSign, path: "/admin?tab=wallet" },
    { key: "payments", label: "Payment Dashboard", icon: ClipboardList, path: "/admin?tab=payments" },
    { key: "tickets", label: "Support Tickets", icon: TicketCheck, path: "/admin?tab=tickets" },
    { key: "reports", label: "Reports & Issues", icon: AlertTriangle, path: "/admin?tab=reports" },
    { key: "analytics", label: "Analytics", icon: BarChart3, path: "/admin?tab=analytics" },
    { key: "services", label: "Services", icon: Settings, path: "/admin?tab=services" },
  ],
  super_admin: [
    { key: "dashboard", label: "Dashboard", icon: LayoutDashboard, path: "/super-admin" },
    { key: "analytics", label: "Analytics", icon: BarChart3, path: "/super-admin?tab=analytics" },
    { key: "services", label: "Services", icon: Settings, path: "/super-admin?tab=services" },
    { key: "payments", label: "Payments", icon: ClipboardList, path: "/super-admin?tab=payments" },
    { key: "branding", label: "Branding", icon: Palette, path: "/super-admin?tab=branding" },
    { key: "platform", label: "Platform Settings", icon: Settings, path: "/super-admin?tab=platform" },
    { key: "fares", label: "Fares & Routes", icon: MapPin, path: "/super-admin?tab=fares" },
    { key: "documents", label: "Documents", icon: FolderOpen, path: "/super-admin?tab=documents" },
    { key: "users", label: "Users", icon: Users, path: "/super-admin?tab=users" },
    { key: "drivers", label: "Drivers", icon: Car, path: "/super-admin?tab=drivers" },
    { key: "rides", label: "Rides", icon: Route, path: "/super-admin?tab=rides" },
    { key: "reports", label: "Reports & Issues", icon: AlertTriangle, path: "/super-admin?tab=reports" },
    { key: "cms", label: "Content", icon: FileText, path: "/super-admin?tab=cms" },
  ],
};

const roleLabels = {
  customer: "Customer",
  driver: "Driver Partner",
  admin: "Admin Panel",
  super_admin: "Super Admin",
};

const roleColors = {
  customer: "bg-forest",
  driver: "bg-blue-600",
  admin: "bg-slate-800",
  super_admin: "bg-slate-900",
};

export default function DashboardLayout({ children, activeTab, onTabChange }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const role = user?.role || "customer";
  const menu = menuConfig[role] || menuConfig.customer;
  const sidebarColor = roleColors[role] || roleColors.customer;

  const token = localStorage.getItem("cv_token");
  const fetchUnread = useCallback(async () => {
    if (!token) return;
    try {
      const res = await safeFetch(`${API}/notifications/unread-count`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) { const d = await res.json(); setUnreadCount(d.count); }
    } catch {}
  }, [token]);

  useEffect(() => { fetchUnread(); const iv = setInterval(fetchUnread, 30000); return () => clearInterval(iv); }, [fetchUnread]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const handleNavClick = (item) => {
    setSidebarOpen(false);
    if (onTabChange) {
      onTabChange(item.key);
    } else {
      navigate(item.path);
    }
  };

  const isActive = (key) => activeTab === key;

  return (
    <div className="min-h-screen bg-slate-50 font-body flex">
      {/* Sidebar */}
      <aside 
        className={`fixed inset-y-0 left-0 z-40 w-64 ${sidebarColor} transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo & Role */}
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_cabvalley-ride/artifacts/2vw1iw9l_Untitled%20design-14.png" 
              alt="Cab Valley" 
              className="h-8 w-auto brightness-0 invert" 
            />
          </div>
          <div className="mt-3 flex items-center gap-2">
            {role === "super_admin" && <Crown className="w-4 h-4 text-amber" />}
            <span className="text-xs text-white/60 uppercase tracking-wider">{roleLabels[role]}</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="p-3 space-y-1 overflow-y-auto max-h-[calc(100vh-200px)]">
          {menu.map((item) => (
            <button
              key={item.key}
              data-testid={`nav-${item.key}`}
              onClick={() => handleNavClick(item)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all ${
                isActive(item.key)
                  ? "bg-white/15 text-white font-medium"
                  : "text-white/60 hover:bg-white/10 hover:text-white"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {isActive(item.key) && <ChevronRight className="w-4 h-4 ml-auto" />}
            </button>
          ))}
        </nav>

        {/* User Profile & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10">
          {/* Language Switcher */}
          <div className="mb-3">
            <LanguageSwitcher variant="minimal" className="w-full justify-start" />
          </div>
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center">
              <span className="text-white font-medium text-sm">
                {user?.name?.[0]?.toUpperCase() || "U"}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/50 truncate">{user?.email || user?.phone}</p>
            </div>
          </div>
          <Button
            data-testid="sidebar-logout-btn"
            variant="ghost"
            className="w-full justify-start text-white/60 hover:text-white hover:bg-white/10"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="px-4 h-14 flex items-center justify-between">
            <button 
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="p-2 -ml-2 text-slate-600"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
            <img 
              src="https://customer-assets.emergentagent.com/job_cabvalley-ride/artifacts/2vw1iw9l_Untitled%20design-14.png" 
              alt="Cab Valley" 
              className="h-7 w-auto" 
            />
            <button data-testid="notification-bell" onClick={() => onTabChange && onTabChange("notifications")} className="relative p-2 text-slate-600">
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">{unreadCount}</span>
              )}
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden" 
          onClick={() => setSidebarOpen(false)} 
        />
      )}
    </div>
  );
}
